import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { getUtcTimestamp } from '../utils/date';

const OfferComponent = ({ userId, accessToken, onOfferClick, onOffersFetched, children, isProcessing }) => {
    const { refreshAccessToken } = useAuth();
    const [offers, setOffers] = useState([]);
    const [error, setError] = useState('');
    const productId = 2; // Hardcoded for QuizMaster

    const fetchOffers = useCallback(async (token = accessToken) => {
        if (!userId || !token) {
            setError('Please sign in to view offers.');
            return;
        }
        try {
            const data = await apiCall(
                `/offers/?product_id=${productId}&skip=0&limit=100`,
                'GET',
                null,
                true, // Use Aurum API
                null, // No x-user-id header
                token
            );
            setOffers(data || []);
            if (onOffersFetched) {
                onOffersFetched(data || []);
            }
            setError('');
        } catch (error) {
            console.error('[OfferComponent] Error fetching offers:', error);
            if (error.message.includes('401')) {
                try {
                    const newToken = await refreshAccessToken();
                    await fetchOffers(newToken); // Retry with new token
                } catch (refreshError) {
                    console.error('[OfferComponent] Token refresh failed:', refreshError);
                    setError('Authentication failed. Please sign in again.');
                }
            } else {
                setError(`Error fetching offers: ${error.message}`);
            }
        }
    }, [userId, accessToken, refreshAccessToken, onOffersFetched]);

    useEffect(() => {
        fetchOffers();
    }, [fetchOffers]);

    const now = Date.now();
    const activeOffers = offers.filter((offer) => {
        const startsAt = getUtcTimestamp(offer.starts_at);
        const endsAt = getUtcTimestamp(offer.ends_at);
        const isAfterStart = startsAt === null || now >= startsAt;
        const isBeforeEnd = endsAt === null || now <= endsAt;
        return isAfterStart && isBeforeEnd;
    });

    const purchasableOffers = activeOffers.filter((offer) => {
        const tags = Array.isArray(offer.tags) ? offer.tags : [];
        const amountValue = Number(offer.amount);
        const hasPrice = !Number.isNaN(amountValue) && amountValue > 0;
        const hasPaidTag = tags.includes('standard') || tags.includes('dollar');
        return hasPrice || hasPaidTag;
    });

    return (
        <div className="offer-container">
            {error && <p className="error-text">{error}</p>}
            {purchasableOffers.length > 0 ? (
                <div className="offer-grid">
                    {purchasableOffers.map((offer) => {
                        const tags = Array.isArray(offer.tags) ? offer.tags : [];
                        const isClickable = tags.includes('standard') || tags.includes('dollar');
                        const amountValue = Number(offer.amount);
                        const hasPrice = !Number.isNaN(amountValue) && amountValue > 0;
                        const formattedAmount = hasPrice
                            ? amountValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                            : null;
                        const codeValue = typeof offer.code === 'string' ? offer.code : '';
                        const displayName = codeValue
                            ? `${codeValue.charAt(0).toUpperCase()}${codeValue.slice(1)}`
                            : 'Offer';
                        const pointsDisplay = typeof offer.points === 'number'
                            ? offer.points.toLocaleString()
                            : offer.points || '--';
                        const isSilverPlan = codeValue && codeValue.toLowerCase() === 'silver';
                        const cardClasses = [
                            'offer-card',
                            isClickable ? 'offer-card--active' : 'offer-card--disabled',
                            isProcessing ? 'offer-card--loading' : '',
                            isSilverPlan ? 'offer-card--featured' : '',
                        ]
                            .filter(Boolean)
                            .join(' ');

                        return (
                            <button
                                type="button"
                                key={offer.id}
                                onClick={() => isClickable && codeValue && onOfferClick(codeValue, tags)}
                                disabled={!isClickable || isProcessing}
                                className={cardClasses}
                            >
                                {isSilverPlan && <span className="offer-card__badge">Most Popular</span>}
                                <div className="offer-card__header">
                                    <span className="offer-card__name">{displayName}</span>
                                    {formattedAmount && <span className="offer-card__price">{formattedAmount}</span>}
                                </div>
                                <div className="offer-card__body">
                                    <span className="offer-card__points">{pointsDisplay} points</span>
                                    {offer.description && <p className="offer-card__description">{offer.description}</p>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <p className="offer-empty-state">No paid offers are available right now. Please check back later.</p>
            )}
            {children}
        </div>
    );
};

export default OfferComponent;